import { QueryResult } from '@apollo/client';
import { useMemo } from 'react';

import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { TenantPresentationConfiguration_SubBrandDisclaimerConfigurationFragment } from '@/modules/presentation/graphql/TenantPresentationConfiguration.generated';
import { getNodes } from '@/utils/graphqlUtils';

import {
  LegalDisclaimersForm_OrganizationDisclaimerConfigurationFragment,
  LegalDisclaimersForm_TenantDisclaimerConfigurationFragment,
  LegalDisclaimersQuery,
  LegalDisclaimersQueryVariables,
  useLegalDisclaimersQuery,
} from './graphql/AdminLegalDisclaimers.generated';

type SubBrandDisclosuresMap = Record<
  string,
  | TenantPresentationConfiguration_SubBrandDisclaimerConfigurationFragment
  | null
  | undefined
>;

export function useAdminLegalDisclaimersFormData(): {
  queryResponse: QueryResult<
    LegalDisclaimersQuery,
    LegalDisclaimersQueryVariables
  >;
  tenantDisclosures:
    | LegalDisclaimersForm_TenantDisclaimerConfigurationFragment
    | null
    | undefined;
  organizationDisclosures:
    | LegalDisclaimersForm_OrganizationDisclaimerConfigurationFragment
    | null
    | undefined;
  subBrandDisclosuresMap: SubBrandDisclosuresMap;
} {
  const { createErrorFeedback } = useFeedback();
  const queryResponse = useLegalDisclaimersQuery({
    onError: createErrorFeedback(
      'There was an error fetching disclaimer details. Please refresh the page to try again.'
    ),
    fetchPolicy: 'cache-and-network',
  });

  const tenantDisclosures = useMemo(
    () => queryResponse.data?.tenantInformation?.disclosuresConfiguration,
    [queryResponse]
  );

  const organizationDisclosures = useMemo(
    () => queryResponse.data?.organizationDisclaimerConfiguration,
    [queryResponse]
  );

  const subBrandDisclosuresMap: SubBrandDisclosuresMap = useMemo(
    () =>
      getNodes(queryResponse.data?.subBrands).reduce<SubBrandDisclosuresMap>(
        (acc, subBrand) => ({
          ...acc,
          [subBrand.id]: subBrand.disclaimerConfiguration || null,
        }),
        {}
      ),
    [queryResponse]
  );

  return {
    queryResponse,
    tenantDisclosures,
    organizationDisclosures,
    subBrandDisclosuresMap,
  };
}
